import apiService from './apiService';
import { resolveMediaUrl } from '../utils/imageUrl';
import {
    extractYouTubeId,
    isDirectVideoUrl,
    classifyAspectRatio,
} from '../utils/videoUtils';

// In-memory cache of resolved HTML5 video dimensions & orientation keyed by URL
const videoMetadataCache = new Map();
const preconnectedOrigins = new Set();

let activeProbeEntry = null;

/**
 * Extract a clean URL string from a string or media object wrapper.
 * Rejects literal "null" / "undefined" strings.
 *
 * @param {string|object|null|undefined} value
 * @returns {string}
 */
function extractCleanUrl(value) {
    if (!value) return '';

    let raw = value;
    if (typeof raw === 'object') {
        raw =
            raw.optimized_url ||
            raw.optimizedUrl ||
            raw.stream_url ||
            raw.streamUrl ||
            raw.url ||
            raw.secure_url ||
            raw.path ||
            raw.src ||
            raw.video_url ||
            raw.videoUrl ||
            '';
    }

    if (typeof raw !== 'string') return '';
    const trimmed = raw.trim();
    if (!trimmed || trimmed === 'null' || trimmed === 'undefined') {
        return '';
    }

    return trimmed;
}

/**
 * Append a deterministic version query parameter for backend `/uploads/...` media paths
 * ONLY when a video-specific `updatedAt` timestamp is present, preserving HTTP 206
 * Partial Content byte-range browser caching across plays while still busting cache
 * when an admin replaces a file at the same path.
 *
 * @param {string|null} resolvedUrl
 * @param {string|number|Date|null} updatedAt
 * @returns {string|null}
 */
function appendMediaCacheVersion(resolvedUrl, updatedAt) {
    if (!resolvedUrl || !updatedAt) return resolvedUrl;
    if (!resolvedUrl.includes('/uploads/') || resolvedUrl.includes('?')) {
        return resolvedUrl;
    }

    const timestamp = new Date(updatedAt).getTime();
    if (Number.isFinite(timestamp) && timestamp > 0) {
        return `${resolvedUrl}?v=${timestamp}`;
    }

    return resolvedUrl;
}

/**
 * Optimize unsigned Cloudinary `/video/upload/` URLs for smooth web streaming:
 * - `f_auto`: delivers WebM/MP4 with faststart (`moov` atom at front)
 * - `vc_auto`: selects optimal hardware-accelerated codec (H.264 / VP9)
 * - `q_auto:good`: prevents raw 40-80 Mbps camera bitrates from choking bandwidth while preserving crisp showroom detail
 * - `w_1920,c_limit`: caps oversized 4K camera uploads at 1080p full HD without upscaling smaller videos
 *
 * Leaves signed URLs (`/s--`), non-Cloudinary URLs, and already-transformed URLs untouched.
 *
 * @param {string|null} url
 * @returns {string|null}
 */
function optimizeVideoDeliveryUrl(url) {
    if (!url || typeof url !== 'string') return url;
    if (!url.includes('res.cloudinary.com/') || !url.includes('/video/upload/')) {
        return url;
    }
    if (url.includes('/s--')) {
        return url;
    }

    const marker = '/video/upload/';
    const idx = url.indexOf(marker);
    if (idx === -1) return url;

    const afterUpload = url.slice(idx + marker.length);
    if (!afterUpload) return url;

    const firstSegment = afterUpload.split('/')[0] || '';
    const hasExistingTransform =
        firstSegment.includes(',') ||
        /^(?:f|q|vc|ac|br|w|h|c|sp|so|eo|du|dpr|fl|e|l|o|r|bo)_/i.test(firstSegment);

    if (hasExistingTransform) {
        return url;
    }

    return `${url.slice(0, idx + marker.length)}f_auto,q_auto:good,vc_auto,w_1920,c_limit/${afterUpload}`;
}

/**
 * Optimize unsigned Cloudinary `/image/upload/` poster thumbnails for instant decode.
 *
 * @param {string|null} url
 * @returns {string|null}
 */
function optimizePosterDeliveryUrl(url) {
    if (!url || typeof url !== 'string') return url;
    if (!url.includes('res.cloudinary.com/') || !url.includes('/image/upload/')) {
        return url;
    }
    if (url.includes('/s--')) {
        return url;
    }

    const marker = '/image/upload/';
    const idx = url.indexOf(marker);
    if (idx === -1) return url;

    const afterUpload = url.slice(idx + marker.length);
    if (!afterUpload) return url;

    const firstSegment = afterUpload.split('/')[0] || '';
    const hasExistingTransform =
        firstSegment.includes(',') ||
        /^(?:f|q|w|h|c|dpr|fl|e|g|ar)_/i.test(firstSegment);

    if (hasExistingTransform) {
        return url;
    }

    return `${url.slice(0, idx + marker.length)}f_auto,q_auto,w_1280,c_limit/${afterUpload}`;
}

/**
 * Parse Vimeo watch or player URLs into an embeddable player URL.
 *
 * @param {string} url
 * @returns {string|null}
 */
function resolveVimeoEmbedUrl(url) {
    if (!url || typeof url !== 'string') return null;
    const trimmed = url.trim();

    const playerMatch = trimmed.match(
        /^https?:\/\/player\.vimeo\.com\/video\/(\d+)/i
    );
    if (playerMatch) {
        const hasQuery = trimmed.includes('?');
        return `${trimmed}${hasQuery ? '&' : '?'}autoplay=1&dnt=1`;
    }

    const standardMatch = trimmed.match(
        /^https?:\/\/(?:www\.)?vimeo\.com\/(?:channels\/[\w-]+\/|groups\/[\w-]+\/videos\/)?(\d+)/i
    );
    if (standardMatch && standardMatch[1]) {
        return `https://player.vimeo.com/video/${standardMatch[1]}?autoplay=1&dnt=1`;
    }

    return null;
}

/**
 * Detect other known third-party iframe embed player URLs.
 *
 * @param {string} url
 * @returns {boolean}
 */
function isThirdPartyEmbedUrl(url) {
    if (!url || typeof url !== 'string') return false;
    const lower = url.toLowerCase();
    return (
        lower.includes('player.vimeo.com/') ||
        lower.includes('dailymotion.com/embed') ||
        lower.includes('facebook.com/plugins/video') ||
        lower.includes('tiktok.com/embed') ||
        (lower.includes('instagram.com/') && lower.includes('/embed'))
    );
}

/**
 * Infer initial video orientation before HTML5 metadata loads.
 *
 * @param {object} item
 * @param {string} rawUrl
 * @param {string|null} youtubeId
 * @param {string|null} vimeoEmbedUrl
 * @returns {'landscape'|'portrait'|'square'|'unknown'}
 */
function inferTourVideoOrientation(item, rawUrl, youtubeId, vimeoEmbedUrl) {
    const rawW = item.width || item.video_width || item.videoWidth;
    const rawH = item.height || item.video_height || item.videoHeight;
    const fromDims = classifyAspectRatio(rawW, rawH);
    if (fromDims && fromDims.orientation) {
        return fromDims.orientation;
    }

    const rawAspect = item.aspect_ratio || item.aspectRatio;
    if (typeof rawAspect === 'number' && rawAspect > 0) {
        const fromNum = classifyAspectRatio(rawAspect, 1);
        if (fromNum && fromNum.orientation) return fromNum.orientation;
    } else if (typeof rawAspect === 'string' && rawAspect.trim()) {
        const parts = rawAspect.trim().split(/[:/xX]/);
        if (parts.length === 2) {
            const fromPair = classifyAspectRatio(parts[0], parts[1]);
            if (fromPair && fromPair.orientation) return fromPair.orientation;
        }
    }

    const explicitOrientation = String(
        item.orientation || item.video_orientation || item.videoOrientation || ''
    )
        .trim()
        .toLowerCase();

    if (explicitOrientation === 'landscape' || explicitOrientation === 'horizontal') {
        return 'landscape';
    }
    if (explicitOrientation === 'portrait' || explicitOrientation === 'vertical') {
        return 'portrait';
    }
    if (explicitOrientation === 'square') {
        return 'square';
    }

    if (youtubeId) {
        const lowerUrl = String(rawUrl || '').toLowerCase();
        return lowerUrl.includes('/shorts/') ? 'portrait' : 'landscape';
    }

    if (vimeoEmbedUrl) {
        return 'landscape';
    }

    if (rawUrl) {
        const lower = rawUrl.toLowerCase();
        if (lower.includes('tiktok.com') || lower.includes('/reel/')) {
            return 'portrait';
        }
        if (lower.includes('dailymotion.com')) {
            return 'landscape';
        }
    }

    return 'unknown';
}

/**
 * Service for fetching and pre-warming the dynamic Showroom Tour Video configured
 * in the Admin Dashboard. Never returns static, hardcoded, or dummy fallback videos.
 */
class ShowroomTourVideoService {
    constructor() {
        this._primaryRouteMissing = false;
        this._settingsRouteMissing = false;
    }

    /**
     * Warm DNS + TCP + TLS connection to the media host before user clicks Play.
     *
     * @param {string|null} mediaUrl
     */
    preconnectMediaOrigin(mediaUrl) {
        if (
            !mediaUrl ||
            typeof window === 'undefined' ||
            typeof document === 'undefined' ||
            !document.head
        ) {
            return;
        }

        try {
            const parsed = new URL(mediaUrl, window.location.origin);
            if (
                !parsed.origin ||
                parsed.origin === 'null' ||
                parsed.origin === window.location.origin ||
                preconnectedOrigins.has(parsed.origin)
            ) {
                return;
            }

            preconnectedOrigins.add(parsed.origin);

            const dnsLink = document.createElement('link');
            dnsLink.rel = 'dns-prefetch';
            dnsLink.href = parsed.origin;
            document.head.appendChild(dnsLink);

            const preconnectLink = document.createElement('link');
            preconnectLink.rel = 'preconnect';
            preconnectLink.href = parsed.origin;
            document.head.appendChild(preconnectLink);
        } catch (_) {
            // Ignore malformed URLs
        }
    }

    /**
     * Retrieve cached HTML5 video dimensions & orientation if already probed.
     *
     * @param {string|null} videoSrc
     * @returns {{ width: number, height: number, orientation: string } | null}
     */
    getCachedVideoMetadata(videoSrc) {
        if (!videoSrc) return null;
        return videoMetadataCache.get(videoSrc) || null;
    }

    /**
     * Store measured video dimensions & classified orientation for instant modal sizing.
     *
     * @param {string|null} videoSrc
     * @param {number} width
     * @param {number} height
     * @returns {{ width: number, height: number, orientation: string } | null}
     */
    recordVideoMetadata(videoSrc, width, height) {
        if (!videoSrc || !(width > 0) || !(height > 0)) return null;
        const profile = classifyAspectRatio(width, height);
        if (!profile || !profile.orientation) return null;

        const entry = {
            width: Number(width),
            height: Number(height),
            orientation: profile.orientation,
        };
        videoMetadataCache.set(videoSrc, entry);
        return entry;
    }

    /**
     * Release any in-flight background metadata probe immediately so the active
     * modal `<video preload="auto">` gets exclusive socket and bandwidth priority.
     */
    cancelActiveMetadataProbe() {
        if (activeProbeEntry && typeof activeProbeEntry.cleanup === 'function') {
            activeProbeEntry.cleanup(null);
        }
    }

    /**
     * Lightweight background probe that fetches only container headers (`preload="metadata"`),
     * warms the MP4 `moov` seek table in the browser's HTTP 206 byte-range cache, resolves
     * exact orientation before the user clicks Play, and immediately releases the probe element
     * so zero sockets or decoders remain open on the page.
     *
     * @param {string|null} videoSrc
     * @returns {Promise<{ width: number, height: number, orientation: string } | null>}
     */
    probeVideoMetadata(videoSrc) {
        if (
            !videoSrc ||
            typeof window === 'undefined' ||
            typeof document === 'undefined'
        ) {
            return Promise.resolve(null);
        }

        this.preconnectMediaOrigin(videoSrc);

        const cached = videoMetadataCache.get(videoSrc);
        if (cached) {
            return Promise.resolve(cached);
        }

        // Respect user Data Saver preference on metered mobile connections
        const connection =
            navigator.connection ||
            navigator.mozConnection ||
            navigator.webkitConnection;
        if (connection && connection.saveData) {
            return Promise.resolve(null);
        }

        if (activeProbeEntry && activeProbeEntry.url === videoSrc) {
            return activeProbeEntry.promise;
        }

        this.cancelActiveMetadataProbe();

        let probeEl = null;
        let timeoutId = null;
        let settled = false;
        let resolvePromise = null;

        const cleanup = (result) => {
            if (settled) return;
            settled = true;

            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }

            if (probeEl) {
                probeEl.onloadedmetadata = null;
                probeEl.onerror = null;
                try {
                    probeEl.pause();
                    probeEl.removeAttribute('src');
                    probeEl.load();
                } catch (_) {}
                probeEl = null;
            }

            if (activeProbeEntry && activeProbeEntry.url === videoSrc) {
                activeProbeEntry = null;
            }

            if (resolvePromise) {
                resolvePromise(result || null);
            }
        };

        const promise = new Promise((resolve) => {
            resolvePromise = resolve;

            try {
                probeEl = document.createElement('video');
                probeEl.muted = true;
                probeEl.playsInline = true;
                probeEl.preload = 'metadata';

                probeEl.onloadedmetadata = () => {
                    const meta =
                        probeEl &&
                        probeEl.videoWidth > 0 &&
                        probeEl.videoHeight > 0
                            ? this.recordVideoMetadata(
                                  videoSrc,
                                  probeEl.videoWidth,
                                  probeEl.videoHeight
                              )
                            : null;
                    cleanup(meta);
                };

                probeEl.onerror = () => {
                    cleanup(null);
                };

                timeoutId = setTimeout(() => {
                    cleanup(null);
                }, 6000);

                probeEl.src = videoSrc;
            } catch (_) {
                cleanup(null);
            }
        });

        activeProbeEntry = {
            url: videoSrc,
            promise,
            cleanup,
        };

        return promise;
    }

    /**
     * Extract the raw showroom tour video configuration object from an API response envelope.
     *
     * @param {any} response
     * @returns {object|null}
     */
    extractRawConfig(response) {
        if (!response || typeof response !== 'object') return null;

        const payload = response.data !== undefined ? response.data : response;
        if (!payload) return null;

        if (typeof payload === 'string') {
            return payload.trim() ? { video_url: payload.trim() } : null;
        }

        if (typeof payload !== 'object' || Array.isArray(payload)) {
            return null;
        }

        const keyedCandidates = [
            'showroomTourVideo',
            'showroom_tour_video',
            'tourVideo',
            'tour_video',
            'showroomVideo',
            'showroom_video',
            'video',
        ];

        for (const key of keyedCandidates) {
            if (Object.prototype.hasOwnProperty.call(payload, key)) {
                const entry = payload[key];
                if (!entry) return null;
                if (typeof entry === 'string') {
                    return entry.trim() ? { video_url: entry.trim() } : null;
                }
                if (typeof entry === 'object' && !Array.isArray(entry)) {
                    return entry;
                }
            }
        }

        // Check nested settings container (e.g. GET /api/ecommerce/settings)
        const settingsObj = payload.settings || response.settings;
        if (settingsObj && typeof settingsObj === 'object') {
            for (const key of keyedCandidates) {
                if (Object.prototype.hasOwnProperty.call(settingsObj, key)) {
                    const entry = settingsObj[key];
                    if (!entry) return null;
                    if (typeof entry === 'string') {
                        return entry.trim() ? { video_url: entry.trim() } : null;
                    }
                    if (typeof entry === 'object' && !Array.isArray(entry)) {
                        return entry;
                    }
                }
            }

            if (
                settingsObj.showroom_tour_video_url ||
                settingsObj.showroomTourVideoUrl
            ) {
                return {
                    video_url:
                        settingsObj.showroom_tour_video_url ||
                        settingsObj.showroomTourVideoUrl,
                    source_type:
                        settingsObj.showroom_tour_video_source_type ||
                        settingsObj.showroomTourVideoSourceType ||
                        '',
                    thumbnail_url:
                        settingsObj.showroom_tour_video_thumbnail ||
                        settingsObj.showroomTourVideoThumbnail ||
                        '',
                    title:
                        settingsObj.showroom_tour_video_title ||
                        settingsObj.showroomTourVideoTitle ||
                        '',
                    is_active:
                        settingsObj.showroom_tour_video_active ??
                        settingsObj.showroomTourVideoActive ??
                        true,
                    // Only use video-specific updated timestamp so unrelated store setting edits
                    // never bust the browser's cached video byte ranges
                    updatedAt:
                        settingsObj.showroom_tour_video_updated_at ||
                        settingsObj.showroomTourVideoUpdatedAt ||
                        null,
                };
            }
        }

        // Direct flat object returned inside `data`
        const hasDirectVideoKeys =
            Object.prototype.hasOwnProperty.call(payload, 'optimized_video_url') ||
            Object.prototype.hasOwnProperty.call(payload, 'optimizedVideoUrl') ||
            Object.prototype.hasOwnProperty.call(payload, 'stream_url') ||
            Object.prototype.hasOwnProperty.call(payload, 'streamUrl') ||
            Object.prototype.hasOwnProperty.call(payload, 'video_url') ||
            Object.prototype.hasOwnProperty.call(payload, 'videoUrl') ||
            Object.prototype.hasOwnProperty.call(payload, 'uploaded_video_url') ||
            Object.prototype.hasOwnProperty.call(payload, 'uploadedVideoUrl') ||
            Object.prototype.hasOwnProperty.call(payload, 'file_url') ||
            Object.prototype.hasOwnProperty.call(payload, 'fileUrl') ||
            Object.prototype.hasOwnProperty.call(payload, 'video_file') ||
            Object.prototype.hasOwnProperty.call(payload, 'videoFile') ||
            Object.prototype.hasOwnProperty.call(payload, 'url');

        if (hasDirectVideoKeys) {
            return payload;
        }

        return null;
    }

    /**
     * Normalize a raw showroom tour video record into a clean player-ready object.
     * Supports uploaded video files (`/uploads/...`, Cloudinary, MP4/WebM/MOV) and
     * external video URLs (YouTube, Vimeo, embed players, or direct remote video streams).
     *
     * @param {object|null} item
     * @returns {object|null}
     */
    normalizeVideoConfig(item) {
        if (!item || typeof item !== 'object') return null;

        const rawStatus = String(item.status || '').trim().toLowerCase();
        const isActive =
            item.is_active !== false &&
            item.isActive !== false &&
            item.is_enabled !== false &&
            item.isEnabled !== false &&
            item.enabled !== false &&
            item.is_published !== false &&
            item.isPublished !== false &&
            rawStatus !== 'inactive' &&
            rawStatus !== 'disabled' &&
            rawStatus !== 'removed' &&
            rawStatus !== 'deleted' &&
            rawStatus !== 'draft';

        if (!isActive) {
            return null;
        }

        const rawSourceType = String(
            item.source_type ||
                item.sourceType ||
                item.video_type ||
                item.videoType ||
                ''
        )
            .trim()
            .toLowerCase();

        const isExplicitUpload =
            rawSourceType === 'upload' ||
            rawSourceType === 'uploaded' ||
            rawSourceType === 'file' ||
            rawSourceType === 'local';

        const preferredStreamCandidate = extractCleanUrl(
            item.optimized_video_url ||
                item.optimizedVideoUrl ||
                item.stream_url ||
                item.streamUrl
        );

        const rawUrl =
            preferredStreamCandidate ||
            (isExplicitUpload
                ? extractCleanUrl(
                      item.uploaded_video_url ||
                          item.uploadedVideoUrl ||
                          item.file_url ||
                          item.fileUrl ||
                          item.video_file ||
                          item.videoFile ||
                          item.video_path ||
                          item.videoPath ||
                          item.video_url ||
                          item.videoUrl ||
                          item.url ||
                          item.src
                  )
                : extractCleanUrl(
                      item.video_url ||
                          item.videoUrl ||
                          item.uploaded_video_url ||
                          item.uploadedVideoUrl ||
                          item.file_url ||
                          item.fileUrl ||
                          item.video_file ||
                          item.videoFile ||
                          item.video_path ||
                          item.videoPath ||
                          item.url ||
                          item.src
                  ));

        if (!rawUrl) {
            return null;
        }

        const updatedAt =
            item.video_updated_at ||
            item.videoUpdatedAt ||
            item.updatedAt ||
            item.updated_at ||
            null;
        const id = String(item._id || item.id || 'showroom-tour-video');

        const youtubeId = isExplicitUpload ? null : extractYouTubeId(rawUrl);
        const vimeoEmbedUrl =
            !isExplicitUpload && !youtubeId ? resolveVimeoEmbedUrl(rawUrl) : null;
        const isOtherEmbed =
            !isExplicitUpload &&
            !youtubeId &&
            !vimeoEmbedUrl &&
            isThirdPartyEmbedUrl(rawUrl);

        let playerType = 'native';
        let videoSrc = null;
        let rawVideoSrc = null;
        let embedUrl = null;

        if (youtubeId) {
            playerType = 'youtube';
            embedUrl = `https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&playsinline=1&rel=0&modestbranding=1`;
        } else if (vimeoEmbedUrl) {
            playerType = 'embed';
            embedUrl = vimeoEmbedUrl;
        } else if (isOtherEmbed) {
            playerType = 'embed';
            embedUrl = rawUrl;
        } else {
            // Uploaded video file or direct remote video URL (MP4, WebM, MOV, Cloudinary, etc.)
            const resolved = resolveMediaUrl(rawUrl);
            if (!resolved) {
                return null;
            }
            playerType = 'native';
            rawVideoSrc = appendMediaCacheVersion(resolved, updatedAt);
            videoSrc = optimizeVideoDeliveryUrl(rawVideoSrc);
        }

        // Resolve optional poster/thumbnail ONLY from real API data (no static fallback image)
        const rawPoster = extractCleanUrl(
            item.thumbnail_url ||
                item.thumbnailUrl ||
                item.poster_url ||
                item.posterUrl ||
                item.poster ||
                item.thumbnail
        );
        const resolvedPoster = rawPoster
            ? optimizePosterDeliveryUrl(
                  appendMediaCacheVersion(resolveMediaUrl(rawPoster), updatedAt)
              )
            : null;

        const cachedMeta = videoSrc
            ? this.getCachedVideoMetadata(videoSrc) ||
              this.getCachedVideoMetadata(rawVideoSrc)
            : null;

        const initialOrientation =
            cachedMeta?.orientation ||
            inferTourVideoOrientation(item, rawUrl, youtubeId, vimeoEmbedUrl);

        const mimeType = String(
            item.video_mime_type ||
                item.mime_type ||
                item.mimeType ||
                item.file_type ||
                ''
        )
            .trim()
            .toLowerCase();

        const title =
            String(item.title || item.heading || '').trim() ||
            'Rahber Corporation Flagship Showroom Walkthrough';
        const badge =
            String(item.badge || item.eyebrow || '').trim() ||
            'Official Showroom Tour Walkthrough';
        const buttonText =
            String(
                item.button_text ||
                    item.buttonText ||
                    item.cta_text ||
                    item.ctaText ||
                    ''
            ).trim() || 'Watch Showroom Tour Video';

        return {
            id,
            playerType,
            sourceType: youtubeId
                ? 'youtube'
                : isExplicitUpload || isDirectVideoUrl(rawUrl)
                  ? 'upload'
                  : playerType,
            videoSrc,
            rawVideoSrc,
            embedUrl,
            youtubeId: youtubeId || null,
            posterUrl: resolvedPoster || null,
            mimeType: mimeType || null,
            title,
            badge,
            buttonText,
            initialOrientation,
            updatedAt,
            // Stable key tied to actual media source so tab-focus/SSE refreshes never remount an active stream
            playbackKey: `${id}-${rawVideoSrc || embedUrl || ''}`,
        };
    }

    /**
     * Fetch the active Showroom Tour Video from the backend API.
     *
     * - Queries `GET /api/ecommerce/showroom-tour-video` first.
     * - Falls back to `GET /api/ecommerce/settings` if the dedicated endpoint returns 404.
     * - Returns `null` when no video is configured or when removed by the admin (never dummy data).
     * - Throws on real network or 5xx server errors so the UI can display an error state with Retry.
     *
     * @param {object} [options]
     * @param {boolean} [options.forceRefresh=false]
     * @returns {Promise<object|null>}
     */
    async getShowroomTourVideo({ forceRefresh = false } = {}) {
        if (forceRefresh) {
            this._primaryRouteMissing = false;
            this._settingsRouteMissing = false;
        }

        const params = forceRefresh ? { _t: Date.now() } : {};

        // 1. Primary dedicated public endpoint: GET /api/ecommerce/showroom-tour-video
        if (!this._primaryRouteMissing) {
            try {
                const response = await apiService.get(
                    '/ecommerce/showroom-tour-video',
                    params
                );

                if (response && response.success === false) {
                    throw {
                        message:
                            response.message ||
                            'Unable to load showroom tour video.',
                        status: 500,
                    };
                }

                const rawConfig = this.extractRawConfig(response);
                return this.normalizeVideoConfig(rawConfig);
            } catch (error) {
                if (error && Number(error.status) === 404) {
                    this._primaryRouteMissing = true;
                } else {
                    console.warn(
                        '[ShowroomTourVideoService] Failed to fetch showroom tour video:',
                        error?.message || error
                    );
                    throw error;
                }
            }
        }

        // 2. Secondary fallback: check GET /api/ecommerce/settings if showroom tour video is stored in settings
        if (!this._settingsRouteMissing) {
            try {
                const settingsResponse = await apiService.get(
                    '/ecommerce/settings',
                    params
                );

                if (settingsResponse && settingsResponse.success === false) {
                    return null;
                }

                const rawFromSettings = this.extractRawConfig(settingsResponse);
                return this.normalizeVideoConfig(rawFromSettings);
            } catch (error) {
                if (error && Number(error.status) === 404) {
                    this._settingsRouteMissing = true;
                    return null;
                }
                console.warn(
                    '[ShowroomTourVideoService] Failed to check settings for showroom tour video:',
                    error?.message || error
                );
                throw error;
            }
        }

        return null;
    }
}

const showroomTourVideoService = new ShowroomTourVideoService();
export default showroomTourVideoService;
